import React, { useContext, useState, useEffect } from 'react';
import moment from 'moment-timezone';
import {
  OrderPhysicianPick,
  OrderVerbalDatePick,
  CreateOrderButtons,
  OrderConvoPick,
  OrderType,
} from '@mummssoftware/common-ui';
import {
  OrdersGlobalContext,
  VFGlobalContext,
  GlobalContext,
  OPEN_WIDGET,
} from '@mummssoftware/global-context';
import { isFreqOrder } from '@mummssoftware/utils/orders/orders-utils';
import { CarePlanFrequency, error } from '@mummssoftware/utils/constants';
import {
  SET_ORDER_PHYSICIAN,
  SET_VERBAL_ORDER_DATE,
  SET_CONVO_TYPE,
  OPEN_CREATE_ORDER,
} from '@mummssoftware/global-context/OrdersGlobalContext';
import {
  ADD_ORDER_DATE,
  SET_ORDERS_MODE,
} from '@mummssoftware/global-context/VFGlobalContext';
import {
  getMinVerbalDate,
  conversationTypes,
  mapOrderName,
} from '@mummssoftware/utils/orders';
import { isAgencyUserDoctorRoleGroup } from '@mummssoftware/utils/utils';
import { Context } from '../../context';
import { CheckMenuWrapper, CheckOrderInfo } from './WrapperDiv';

export const CreateOrder = () => {
  const {
    convoType,
    verbalDate,
    admitPhysician,
    orderType,
    dispatchOrders,
    cancelOrderWorkflow,
  } = useContext(OrdersGlobalContext);
  const {
    doctorsGlobal,
    patientGlobal,
    patientDateRestrictions,
    dispatch: dispatchGlobal,
  } = useContext(GlobalContext);
  const { dispatchVF } = useContext(VFGlobalContext);
  const { currentUser } = useContext(Context);

  const medNPOrderDoctorBins: string = window.env
    ?.medNPOrderDoctorBins as string;
  const medPAOrderDoctorBins: string = window.env
    ?.medPAOrderDoctorBins as string;
  const isPhysicianRoleGroup: boolean = isAgencyUserDoctorRoleGroup(
    currentUser,
    medNPOrderDoctorBins,
    medPAOrderDoctorBins,
  ) as boolean;

  /**
   * Create Orders state
   */

  const [closeOrder, setCloseOrder] = useState<boolean>(false);

  /**
   * Verbal Date Minimum
   */

  // referralDate is the MIN date for VERBAL DATE for LOC and LC
  const referralDate =
    patientGlobal && moment((patientGlobal as mumms.patient).referralDate);

  const verbalDateMin: any = getMinVerbalDate(
    referralDate,
    patientDateRestrictions,
    orderType,
  );

  /**
   * Global Orders constants
   */
  const verbalDateSeverity = verbalDate ? null : error;
  const doctorSeverity = admitPhysician ? null : error;
  const approvalError = isPhysicianRoleGroup
    ? false
    : !admitPhysician || !convoType || !!verbalDateSeverity;
  /**
   * Global Orders functions
   */
  const handleVerbalDateChange = (approvaldate: Date | null) => {
    if (isFreqOrder(orderType)) {
      dispatchVF({
        type: ADD_ORDER_DATE,
        payload: approvaldate,
      });
    }
    dispatchOrders({
      type: SET_VERBAL_ORDER_DATE,
      payload: approvaldate || new Date(),
    });
  };

  const handlePhysicianChange = (physician?: any) => {
    dispatchOrders({
      type: SET_ORDER_PHYSICIAN,
      payload: physician,
    });
  };

  const onCancelClick = () => {
    cancelOrderWorkflow();
  };

  const openVisitFrequencyWidget = () => {
    dispatchVF({ type: SET_ORDERS_MODE, payload: true });
    dispatchGlobal({
      type: OPEN_WIDGET,
      payload: {
        componentName: CarePlanFrequency,
      },
    });
  };

  useEffect(() => {
    if (isPhysicianRoleGroup && isFreqOrder(orderType)) {
      dispatchOrders({
        type: SET_VERBAL_ORDER_DATE,
        payload: new Date(),
      });
      dispatchOrders({
        type: OPEN_CREATE_ORDER,
        payload: false,
      });
      openVisitFrequencyWidget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhysicianRoleGroup, orderType]);

  const onCreateDetailsClick = () => {
    setCloseOrder(true);
    if (!approvalError) {
      dispatchOrders({
        type: OPEN_CREATE_ORDER,
        payload: false,
      });
      if (isFreqOrder(orderType)) {
        openVisitFrequencyWidget();
      }
    }
  };

  const handleConversationChange = (conversation?: any) => {
    dispatchOrders({
      type: SET_CONVO_TYPE,
      payload: conversation,
    });
  };

  /**
   * Global Orders UI
   * When isPhysicianRoleGroup: no need for OrderType, OrderVerbalDatePick, OrderConvoPick, OrderPhysicianPick
   */
  const getApprovalComponents = () => {
    if (isPhysicianRoleGroup) {
      return <></>;
    }
    return (
      <>
        <OrderType orderName={(mapOrderName as any)[orderType as any]} />
        <OrderConvoPick
          value={convoType}
          options={conversationTypes}
          handleConversationChange={handleConversationChange}
        />
        <OrderVerbalDatePick
          verbalDate={verbalDate}
          handleVerbalDateChange={handleVerbalDateChange}
          maximumDate={Date.now()}
          minimumDate={verbalDateMin}
          severity={closeOrder ? verbalDateSeverity : null}
          error={undefined}
        />
        <OrderPhysicianPick
          checkDoctor={(admitPhysician as mumms.Doctor) || undefined}
          doctorsOptions={(doctorsGlobal as mumms.Doctor[]) || []}
          handlePhysicianChange={handlePhysicianChange}
          severity={closeOrder ? doctorSeverity : null}
          error={undefined}
        />
      </>
    );
  };

  return (
    <CheckMenuWrapper>
      <CreateOrderButtons
        onCancelClick={onCancelClick}
        onCreateDetailsClick={onCreateDetailsClick}
      />
      <CheckOrderInfo id="orders-create">
        {getApprovalComponents()}
      </CheckOrderInfo>
    </CheckMenuWrapper>
  );
};
